// MyProfile — global user profile with reusable documents (passports, visas,
// insurance, emergency contacts, allergies, dietary). Available across all
// trips so the user doesn't have to re-enter them for each new trip.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Plus, Trash2, FileText, Phone, Utensils, Save,
  Eye, X, ExternalLink, Upload, FileImage, File as FileIcon,
} from "lucide-react";
import { api } from "../auth/AuthContext";

const DOC_KINDS = [
  { value: "passport", label: "Pasaporte", icon: "📕" },
  { value: "visa", label: "Visa", icon: "🛂" },
  { value: "insurance", label: "Seguro de viaje", icon: "🛡️" },
  { value: "id-card", label: "Cédula / DNI", icon: "🪪" },
  { value: "vaccine", label: "Vacuna internacional", icon: "💉" },
  { value: "driver-license", label: "Licencia de conducir", icon: "🚗" },
  { value: "other", label: "Otro", icon: "📄" },
];

const kindMeta = (k) => DOC_KINDS.find((d) => d.value === k) || DOC_KINDS[6];

function uid() { return `doc-${Math.random().toString(36).slice(2, 9)}`; }

// Read a File as data URL (base64)
const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const isPdfFile = (f) => {
  if (!f) return false;
  if (f.mime && f.mime.includes("pdf")) return true;
  if (f.type === "pdf") return true;
  if (f.url && f.url.toLowerCase().endsWith(".pdf")) return true;
  if (typeof f.data === "string" && f.data.startsWith("data:application/pdf")) return true;
  return false;
};

const fileSrc = (f) => f.data || f.url || "";

export default function MyProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [profile, setProfile] = useState({
    documents: [],
    emergency_contacts: [],
    allergies: "",
    dietary: "",
    trip_credits: { free_trip_used: false, paid_trips: 0 },
  });

  useEffect(() => {
    api("/api/profile")
      .then((data) => setProfile((p) => ({ ...p, ...data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api("/api/profile", { method: "PUT", body: JSON.stringify(profile) });
    } finally {
      setSaving(false);
    }
  };

  const addDoc = (kind) => setProfile((p) => ({
    ...p,
    documents: [...p.documents, { id: uid(), kind, label: kindMeta(kind).label, owner: "", number: "", expires: "", notes: "", files: [] }],
  }));
  const updateDoc = (id, patch) => setProfile((p) => ({
    ...p,
    documents: p.documents.map((d) => (d.id === id ? { ...d, ...patch } : d)),
  }));
  const removeDoc = (id) => setProfile((p) => ({ ...p, documents: p.documents.filter((d) => d.id !== id) }));

  const addFileToDoc = async (docId, fileList) => {
    if (!fileList || !fileList.length) return;
    const arr = Array.from(fileList);
    const newFiles = await Promise.all(arr.map(async (f) => ({
      type: f.type.startsWith("image/") ? "image" : (f.type === "application/pdf" ? "pdf" : "file"),
      mime: f.type || "application/octet-stream",
      data: await readAsDataURL(f),
      label: f.name,
    })));
    setProfile((p) => ({
      ...p,
      documents: p.documents.map((d) => d.id === docId ? { ...d, files: [...(d.files || []), ...newFiles] } : d),
    }));
  };

  const removeFile = (docId, idx) => setProfile((p) => ({
    ...p,
    documents: p.documents.map((d) => d.id === docId
      ? { ...d, files: (d.files || []).filter((_, i) => i !== idx) }
      : d),
  }));

  if (loading) {
    return <div className="container-app pt-6 text-center text-sm text-[hsl(var(--muted))]">Cargando…</div>;
  }

  const credits = profile.trip_credits || {};
  const freeAvailable = !credits.free_trip_used;

  return (
    <div className="container-app pt-6 pb-32" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/viajes")}
          className="w-9 h-9 rounded-full flex items-center justify-center bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]"
          aria-label="Volver"
          data-testid="profile-back-btn"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Cuenta</p>
          <h1 className="text-2xl font-bold leading-tight">Mi Perfil</h1>
        </div>
      </div>

      {/* Credits card */}
      <div
        className="rounded-2xl p-4 mb-6 border"
        style={{
          background: freeAvailable ? "hsl(140 60% 96%)" : "hsl(var(--bg-card))",
          borderColor: freeAvailable ? "hsl(140 50% 80%)" : "hsl(var(--line))",
        }}
        data-testid="profile-credits-card"
      >
        <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Créditos de viaje</p>
        {freeAvailable ? (
          <div>
            <p className="font-bold text-lg mt-1" style={{ color: "hsl(140 60% 30%)" }}>
              ✨ 1 viaje gratis disponible
            </p>
            <p className="text-xs text-[hsl(var(--ink-soft))] mt-1">
              Prueba la app con un viaje de hasta 3 días · después US$5 por viaje ilimitado
            </p>
          </div>
        ) : (
          <div>
            <p className="font-semibold text-sm mt-1">Viajes pagados: {credits.paid_trips || 0}</p>
            <p className="text-xs text-[hsl(var(--muted))] mt-1">Compra más créditos para curar nuevos viajes</p>
          </div>
        )}
      </div>

      {/* Documents */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="section-title flex items-center gap-2"><FileText size={14} /> Documentos ({profile.documents.length})</h2>
        </div>
        <p className="text-xs text-[hsl(var(--muted))] mb-3">
          Pasaportes, visas, seguros. Se reutilizan en todos tus viajes — no hace falta cargarlos cada vez.
        </p>
        {profile.documents.map((d) => (
          <DocumentCard
            key={d.id}
            doc={d}
            onUpdate={(patch) => updateDoc(d.id, patch)}
            onRemove={() => removeDoc(d.id)}
            onAddFiles={(files) => addFileToDoc(d.id, files)}
            onRemoveFile={(idx) => removeFile(d.id, idx)}
            onPreviewFile={(f) => setPreviewFile(f)}
          />
        ))}
        <div className="flex flex-wrap gap-2 mt-2">
          {DOC_KINDS.slice(0, 4).map((k) => (
            <button
              key={k.value}
              onClick={() => addDoc(k.value)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] active:scale-95"
              data-testid={`add-doc-${k.value}`}
            >
              <Plus size={11} className="inline mr-1" />{k.label}
            </button>
          ))}
        </div>
      </section>

      {/* Emergency contacts */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="section-title flex items-center gap-2"><Phone size={14} /> Contactos de emergencia ({profile.emergency_contacts.length})</h2>
        </div>
        {profile.emergency_contacts.map((c) => (
          <div key={c.id} className="card-paper p-3 mb-2 flex items-center gap-2" data-testid={`contact-${c.id}`}>
            <input
              value={c.name}
              onChange={(e) => setProfile((p) => ({
                ...p,
                emergency_contacts: p.emergency_contacts.map((x) => x.id === c.id ? { ...x, name: e.target.value } : x),
              }))}
              placeholder="Nombre"
              className="input-field flex-1 text-sm"
              data-testid={`contact-name-${c.id}`}
            />
            <input
              value={c.phone}
              onChange={(e) => setProfile((p) => ({
                ...p,
                emergency_contacts: p.emergency_contacts.map((x) => x.id === c.id ? { ...x, phone: e.target.value } : x),
              }))}
              placeholder="Teléfono"
              className="input-field flex-1 text-sm"
              data-testid={`contact-phone-${c.id}`}
            />
            <button
              onClick={() => setProfile((p) => ({ ...p, emergency_contacts: p.emergency_contacts.filter((x) => x.id !== c.id) }))}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[hsl(var(--muted))]"
              aria-label="Eliminar contacto"
              data-testid={`contact-remove-${c.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          onClick={() => setProfile((p) => ({
            ...p,
            emergency_contacts: [...p.emergency_contacts, { id: uid(), name: "", phone: "", relationship: "" }],
          }))}
          className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-[hsl(var(--line))] text-[hsl(var(--ink-soft))]"
          data-testid="add-contact-btn"
        >
          <Plus size={11} className="inline mr-1" />Contacto
        </button>
      </section>

      {/* Preferences */}
      <section className="mb-8">
        <h2 className="section-title flex items-center gap-2 mb-3"><Utensils size={14} /> Preferencias</h2>
        <label className="flex flex-col gap-1 mb-3 text-xs">
          <span className="text-[hsl(var(--muted))]">Alergias / restricciones médicas</span>
          <input
            value={profile.allergies}
            onChange={(e) => setProfile((p) => ({ ...p, allergies: e.target.value }))}
            placeholder="Ej: penicilina, maní"
            className="input-field"
            data-testid="profile-allergies"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-[hsl(var(--muted))]">Restricciones alimenticias</span>
          <input
            value={profile.dietary}
            onChange={(e) => setProfile((p) => ({ ...p, dietary: e.target.value }))}
            placeholder="Ej: kosher, sin cerdo, vegetariano"
            className="input-field"
            data-testid="profile-dietary"
          />
        </label>
      </section>

      {/* Save button (sticky bottom) */}
      <div className="fixed bottom-4 left-0 right-0 px-4 z-10">
        <button
          onClick={save}
          disabled={saving}
          className="w-full max-w-md mx-auto flex items-center justify-center gap-2 h-12 rounded-full bg-[hsl(var(--accent))] text-white font-semibold shadow-lg active:scale-[0.98] disabled:opacity-60"
          data-testid="profile-save-btn"
        >
          <Save size={16} /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      {/* File preview modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-3"
          onClick={() => setPreviewFile(null)}
          data-testid="file-preview-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center justify-between">
              <p className="font-semibold text-sm truncate pr-2">{previewFile.label || "Archivo"}</p>
              <button onClick={() => setPreviewFile(null)} className="text-[hsl(var(--muted))]" aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            {isPdfFile(previewFile) ? (
              <div className="p-4 text-center">
                <p className="text-sm text-[hsl(var(--muted))] mb-3">PDF</p>
                <a
                  href={fileSrc(previewFile)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--ink))] text-white text-xs font-semibold"
                >
                  <ExternalLink size={14} /> Abrir PDF
                </a>
              </div>
            ) : (
              <img src={fileSrc(previewFile)} alt={previewFile.label || "documento"} className="w-full" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Document card ----------
function DocumentCard({ doc, onUpdate, onRemove, onAddFiles, onRemoveFile, onPreviewFile }) {
  const meta = kindMeta(doc.kind);
  const fileInputRef = useRef(null);
  const files = doc.files || [];

  return (
    <div className="card-paper p-4 mb-3" data-testid={`doc-${doc.id}`}>
      <div className="flex items-start gap-2 mb-3">
        <span className="text-2xl">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          {doc.label && (
            <p className="font-bold text-base leading-tight truncate" data-testid={`doc-label-${doc.id}`}>
              {doc.label}
            </p>
          )}
          <select
            value={doc.kind}
            onChange={(e) => onUpdate({ kind: e.target.value })}
            className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold bg-transparent border-0 outline-none mt-0.5"
            data-testid={`doc-kind-${doc.id}`}
          >
            {DOC_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <button
          onClick={onRemove}
          className="w-8 h-8 rounded-full flex items-center justify-center text-[hsl(var(--muted))] active:scale-95"
          aria-label="Eliminar documento"
          data-testid={`doc-remove-${doc.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Files */}
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid={`doc-files-${doc.id}`}>
          {files.map((f, idx) => (
            <FileThumb
              key={idx}
              file={f}
              onOpen={() => onPreviewFile(f)}
              onRemove={() => onRemoveFile(idx)}
              testId={`doc-file-${doc.id}-${idx}`}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">Etiqueta</span>
          <input
            value={doc.label || ""}
            onChange={(e) => onUpdate({ label: e.target.value })}
            placeholder={`Ej: ${meta.label} · Alan`}
            className="input-field"
            data-testid={`doc-labelinput-${doc.id}`}
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">Titular</span>
          <input
            value={doc.owner || ""}
            onChange={(e) => onUpdate({ owner: e.target.value })}
            placeholder="Ej: Alan Guendelman"
            className="input-field"
            data-testid={`doc-owner-${doc.id}`}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">N° / Referencia</span>
          <input
            value={doc.number || ""}
            onChange={(e) => onUpdate({ number: e.target.value })}
            className="input-field font-mono"
            data-testid={`doc-number-${doc.id}`}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">Vence</span>
          <input
            type="text"
            value={doc.expires || ""}
            onChange={(e) => onUpdate({ expires: e.target.value })}
            placeholder="dd/mm/aaaa"
            className="input-field"
            data-testid={`doc-expires-${doc.id}`}
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">Emitido por</span>
          <input
            value={doc.issuedBy || ""}
            onChange={(e) => onUpdate({ issuedBy: e.target.value })}
            className="input-field"
            data-testid={`doc-issuedby-${doc.id}`}
          />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[hsl(var(--muted))]">Notas</span>
          <textarea
            value={doc.notes || ""}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Coberturas, teléfonos, tips…"
            rows={2}
            className="input-field resize-none"
            data-testid={`doc-notes-${doc.id}`}
          />
        </label>
      </div>

      {/* Upload button */}
      <div className="mt-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            onAddFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid={`doc-fileinput-${doc.id}`}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[hsl(var(--line))] text-xs font-medium text-[hsl(var(--ink-soft))] active:scale-95"
          data-testid={`doc-upload-${doc.id}`}
        >
          <Upload size={14} /> {files.length ? "Añadir otro archivo" : "Subir foto o PDF"}
        </button>
      </div>
    </div>
  );
}

function FileThumb({ file, onOpen, onRemove, testId }) {
  const isPdf = isPdfFile(file);
  const src = fileSrc(file);
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border border-[hsl(var(--line))] bg-[hsl(var(--bg-card))]" data-testid={testId}>
      <button
        onClick={onOpen}
        className="absolute inset-0 flex items-center justify-center text-[hsl(var(--muted))] active:scale-95"
        aria-label="Ver archivo"
      >
        {isPdf ? (
          <div className="flex flex-col items-center gap-1">
            <FileIcon size={28} />
            <span className="text-[10px] font-semibold">PDF</span>
          </div>
        ) : src ? (
          <img src={src} alt={file.label || "documento"} className="w-full h-full object-cover" />
        ) : (
          <FileImage size={28} />
        )}
      </button>
      <button
        onClick={onRemove}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-90"
        aria-label="Quitar archivo"
        data-testid={`${testId}-remove`}
      >
        <X size={12} />
      </button>
    </div>
  );
}
